import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Select from "react-select";
import moment from "moment-timezone";
import axios from "axios";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Loader from "../layout/Loader";
import OrderStatusBadge from "./OrderStatusBadge";
import socket from "../socket/socket";
import refreshDriverApp from "../socket/RefreshDriverApp";
import PermissionGate from "../layout/PermissionGate";
import DetailedOrderLog from "./DetailedOrderLog";
import { formatDecimal } from "../utils/decimalFormat";
import OrderStatusLog from "./details/OrderStatusLog";
import OrderDispatchLog from "./details/OrderDispatchLog";

const OrderDetails = () => {
  const [data, setData] = useState({});
  const [driver, setDriver] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [popup, setPopup] = useState("");
  const [error, setError] = useState("");
  const [showDetailedLog, setShowDetailedLog] = useState(false);
  const [showDispatchLog, setShowDispatchLog] = useState(false);
  const { id } = useParams();
  const token = localStorage.getItem("token");

  const fetchDriverList = (latitude, longitude) => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT}driver-list?latitude=${latitude}&longitude=${longitude}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setDrivers(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchOrderDetails = useCallback(() => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}order/details/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setData(res?.data?.data);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      })
      .catch(console.error);
  }, [id]);
  const forceDispatch = () => {
    setIsLoading(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}order/force-dispatch/${id}`,
        { driverId: driver?.value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          setError("");
          setDriver({});
          fetchOrderDetails();
          refreshDriverApp(driver?.value);
        }
      })
      .catch(console.error);
  };
  const markComplete = () => {
    setIsLoading(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}order/mark-complete/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          fetchOrderDetails();
          refreshDriverApp(data?.driverId);
        }
      })
      .catch(console.error);
  };
  const cancelOrder = () => {
    setIsLoading(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}order/cancel-order/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          fetchOrderDetails();
          refreshDriverApp(data?.driverId);
        }
      })
      .catch(console.error);
  };
  const cancelTrip = () => {
    setIsLoading(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}order/cancel-trip/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          fetchOrderDetails();
          refreshDriverApp(data?.driverId);
          socket.emit("admin:sendOrderRequest", res?.data?.data);
        }
      })
      .catch(console.error);
  };
  const scanOrder = () => {
    setIsLoading(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}order/scan-order/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPopup("");
          fetchOrderDetails();
          refreshDriverApp(data?.driverId);
        }
      })
      .catch(console.error);
  };
  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  useEffect(() => {
    if (data?.status === "0" || data?.status === "-1") {
      fetchDriverList(data?.pickup?.latitude, data?.pickup?.longitude);
    }
  }, [data]);

  useEffect(() => {
    socket.on(`admin:ordersUpdate`, (data) => {
      if (data?.orderId?.trim() !== id) return;
      fetchOrderDetails();
    });
    return () => {
      socket.off(`admin:ordersUpdate`);
    };
  }, []);

  return isLoading === true ? (
    <Loader />
  ) : (
    <div className="d-flex flex-column flex-root">
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
              {/*begin::Container*/}
              <div
                id="kt_toolbar_container"
                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
              >
                {/*begin::Page title*/}
                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                  {/*begin::Title*/}
                  <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                    Order Details
                  </h1>
                  {/*end::Title*/}
                </div>
                {/*end::Page title*/}
                {/*begin::Actions*/}
                <div className="d-flex align-items-center">
                  {/*begin::Edit Button*/}
                  {data?.status === "-1" ||
                  data?.status === "0" ||
                  data?.status === "1" ||
                  data?.status === "2" ||
                  data?.status === "3" ||
                  data?.status === "4" ||
                  data?.status === "5" ? (
                    <PermissionGate required={`edit_order`} fallback={<></>}>
                      <Link
                        to={`/orders/edit-order/${id}`}
                        className="ms-4 btn btn-color-success bg-body me-3"
                      >
                        <i className="bi-duotone bi-pen"></i>
                        Edit
                      </Link>
                    </PermissionGate>
                  ) : (
                    ""
                  )}
                  {/*end::Edit Button*/}
                </div>
                {/*end::Actions*/}
              </div>
              {/*end::Container*/}
            </div>
            {/*end::Toolbar*/}

            <div
              id="kt_content_container"
              className="d-flex flex-column-fluid align-items-start  container-xxl "
            >
              <div className="content flex-row-fluid" id="kt_content">
                <div className="row g-5 g-lg-10">
                  {/*begin::Card*/}
                  <PermissionGate required={`view_order_details`}>
                    <div className="card">
                      {/*begin::Card header*/}
                      <div className="card-header border-0 pt-6">
                        {/*begin::Card title*/}
                        <div className="card-title"></div>
                        {/*begin::Card title*/}
                      </div>
                      {/*end::Card header*/}

                      {/*begin::Card body*/}
                      <div className="card-body py-4">
                        <div className="row">
                          <div className="col">
                            <div className="d-flex flex-wrap flex-sm-nowrap">
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                  <div className="d-flex flex-column">
                                    <div className="align-items-center">
                                      <span className="text-dark-400 text-hover-primary me-1">
                                        {data?.orderId}
                                      </span>
                                    </div>
                                    <div className="align-items-center mb-2">
                                      <span className="text-gray-900 text-hover-primary fs-1 me-1">
                                        {moment(data?.orderDateTime)
                                          .tz(process.env.REACT_APP_TIMEZONE)
                                          .format("HH:mm, DD/MM/YYYY")}
                                      </span>
                                    </div>

                                    <div className="fs-25 pe-2 mb-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        <OrderStatusBadge
                                          status={data?.status}
                                        />
                                      </span>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        AMOUNT
                                      </span>
                                      <p className="text-white-300">
                                        {parseFloat(data?.orderAmount)}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <div className="row">
                                        <div className="col">
                                          <span className="d-flex align-items-center text-gray-400 me-5">
                                            DELIVERY FEE
                                          </span>
                                          <p className="text-white-300">
                                            {formatDecimal(data?.feeInt)}{" "}
                                            {data?.currency ?? "KWD"}
                                          </p>
                                        </div>
                                        <div className="col">
                                          <span className="d-flex align-items-center text-gray-400 text-nowrap">
                                            RELATIVE DISTANCE
                                          </span>
                                          <p className="text-white-300">
                                            {data?.distance ?? 0} Km
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        PAYMENT TYPE
                                      </span>
                                      <p className="text-white-300">
                                        <span className="badge text-white bg-info">
                                          {data?.paymentType}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                  <div className="d-flex flex-column">
                                    <div className="fs-25 pe-2 mb-5">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        ORDER CHANNEL
                                      </span>
                                      <p className="text-white-300">
                                        <span className="badge text-white bg-success">
                                          {data?.orderChannel}
                                        </span>
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        CUSTOMER NAME
                                      </span>
                                      <p className="text-white-300">
                                        {data?.customerName}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        CUSTOMER ADDRESS
                                      </span>
                                      <p className="text-white-300">
                                        {`${data?.area?.value}, Block ${data?.block?.value}, ${data?.street?.value}`}{" "}
                                        <br />
                                        {`Building: ${data?.building}, Jadda: ${data?.jadda}`}{" "}
                                        <br />
                                        {`Apartment: ${data?.apartment}, Floor: ${data?.floor}`}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        CUSTOMER PHONE
                                      </span>
                                      <p className="text-white-300">
                                        {data?.customerPhone}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                  <div className="d-flex flex-column">
                                    <div className="fs-25 pe-2 mb-5">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        MERCHANT
                                      </span>
                                      <p className="text-white-300">
                                        {data?.merchantName}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        BRANCH
                                      </span>
                                      <p className="text-white-300">
                                        {data?.branchName}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        BRANCH LOCATION
                                      </span>
                                      <p className="text-white-300">
                                        {data?.branchLocation}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                {/*begin::Title*/}
                                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                  {/*begin::User*/}
                                  <div className="d-flex flex-column">
                                    {/*begin::Info*/}
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        PICKUP LOCATION
                                      </span>
                                      <p className="text-white-300">
                                        <b>Latitude:</b>{" "}
                                        {data?.pickup?.latitude} <br />
                                        <b>Longitude:</b>{" "}
                                        {data?.pickup?.longitude}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        DESCRIPTION
                                      </span>
                                      <p className="text-white-300">
                                        {data?.description ?? ""}
                                      </p>
                                    </div>
                                    <div className="fs-25 pe-2">
                                      <span className="d-flex align-items-center text-gray-400 me-5">
                                        NOTES
                                      </span>
                                      <p className="text-white-300">
                                        {data?.notes ?? ""}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/*end::Card body*/}
                    </div>
                  </PermissionGate>
                  {/*end::Card*/}
                </div>

                <div className="row g-5 g-lg-10 mt-4">
                  <div className="col p-0 mb-xl-10">
                    <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">
                      Actions
                    </h1>
                    <div className="card py-10">
                      <div className="px-10 mb-5">
                        <div className="row mb-0 text-start">
                          {data?.status === "1" ||
                          data?.status === "2" ||
                          data?.status === "3" ||
                          data?.status === "4" ||
                          data?.status === "5" ? (
                            <PermissionGate
                              required={`mark_complete_order`}
                              fallback={<></>}
                            >
                              <div className="col-md-3">
                                <button
                                  onClick={() => {
                                    setPopup("mark-complete");
                                  }}
                                  className="btn btn-success w-100 text-center"
                                >
                                  Mark as Completed
                                </button>
                              </div>
                            </PermissionGate>
                          ) : (
                            ""
                          )}
                          {data?.status === "1" ||
                          data?.status === "2" ||
                          data?.status === "3" ||
                          data?.status === "4" ||
                          data?.status === "5" ? (
                            <PermissionGate
                              required={`cancel_order`}
                              fallback={<></>}
                            >
                              <div className="col-md-3">
                                <button
                                  onClick={() => {
                                    setPopup("cancel-trip");
                                  }}
                                  className="btn btn-danger w-100 text-center"
                                >
                                  Cancel Trip
                                </button>
                              </div>
                            </PermissionGate>
                          ) : (
                            ""
                          )}
                          {data?.status === "-1" ||
                          data?.status === "0" ||
                          data?.status === "1" ||
                          data?.status === "2" ||
                          data?.status === "3" ||
                          data?.status === "4" ||
                          data?.status === "5" ? (
                            <PermissionGate
                              required={`cancel_order`}
                              fallback={<></>}
                            >
                              <div className="col-md-3">
                                <button
                                  onClick={() => {
                                    setPopup("cancel-order");
                                  }}
                                  className="btn btn-warning w-100 text-center"
                                >
                                  Cancel Order
                                </button>
                              </div>
                            </PermissionGate>
                          ) : (
                            ""
                          )}
                          {data?.status === "1" ||
                          data?.status === "2" ||
                          data?.status === "3" ? (
                            <PermissionGate
                              required={`mark_scanned_order`}
                              fallback={<></>}
                            >
                              <div className="col-md-3">
                                <button
                                  onClick={() => {
                                    setPopup("scan-order");
                                  }}
                                  className="btn btn-primary w-100 text-center"
                                >
                                  Mark as Scanned
                                </button>
                              </div>
                            </PermissionGate>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                      <div className="px-10 mb-5">
                        <div className="fs-25 fw-thin mb-2">FORCE DISPATCH</div>
                        <PermissionGate required={`order_force_dispatch`}>
                          <div className="row mb-0">
                            <div className="col-md-4 mb-3">
                              <Select
                                className="react-select-styled react-select-bordered"
                                classNamePrefix="react-select"
                                value={driver?.value ? driver : null}
                                isDisabled={
                                  data?.status === "0" || data?.status === "-1"
                                    ? false
                                    : true
                                }
                                options={drivers?.map((item) => {
                                  return {
                                    value: item?._id,
                                    label: `${item?.name} - ${
                                      parseFloat(item?.distance)?.toFixed(3) ??
                                      0
                                    }km`,
                                  };
                                })}
                                onChange={(e) => {
                                  setDriver(e);
                                  setError("");
                                }}
                                placeholder="Select driver"
                              />
                              <small className="text-danger">{error}</small>
                            </div>
                            <div className="col-md-2 mb-3">
                              <button
                                onClick={() => {
                                  if (driver?.value) {
                                    setPopup("force-dispatch");
                                  } else {
                                    setError("Please select a driver");
                                  }
                                }}
                                disabled={
                                  data?.status === "0" || data?.status === "-1"
                                    ? false
                                    : true
                                }
                                className="btn btn-primary"
                              >
                                Force Dispatch
                              </button>
                            </div>
                          </div>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                </div>
                <OrderStatusLog id={id} />
                <div className="row g-5 g-lg-10 mt-4">
                  <div className="col p-0 mb-xl-10">
                    <div className="d-flex w-100 justify-content-between mb-5">
                      <div className="d-flex text-start text-dark fw-bold m-0 fs-3">
                        Detailed Order Log
                      </div>
                      <div className="form-check form-switch form-check-custom fv-row">
                        <input
                          className="form-check-input w-45px h-30px"
                          type="checkbox"
                          checked={showDetailedLog}
                          onChange={() => setShowDetailedLog(!showDetailedLog)}
                        />
                        <label className="form-check-label"></label>
                      </div>
                    </div>
                    <PermissionGate required={`view_order_log`}>
                      <div className="card py-7">
                        {/*begin::Card body*/}
                        <div className="card-body py-4 text-center">
                          {/*begin::Table*/}
                          {showDetailedLog && (
                            <DetailedOrderLog
                              id={id}
                              showLog={showDetailedLog}
                            />
                          )}
                          {/*end::Table*/}
                        </div>
                        {/*end::Card body*/}
                      </div>
                    </PermissionGate>
                  </div>
                </div>
                <div className="row g-5 g-lg-10 mt-4">
                  <div className="col p-0 mb-xl-10">
                    <div className="d-flex w-100 justify-content-between mb-5">
                      <div className="d-flex text-start text-dark fw-bold m-0 fs-3">
                        Order Dispatch Log
                      </div>
                      <div className="form-check form-switch form-check-custom fv-row">
                        <input
                          className="form-check-input w-45px h-30px"
                          type="checkbox"
                          checked={showDispatchLog}
                          onChange={() => setShowDispatchLog(!showDispatchLog)}
                        />
                        <label className="form-check-label"></label>
                      </div>
                    </div>
                    <PermissionGate required={`view_order_log`}>
                      <div className="card py-7">
                        {/*begin::Card body*/}
                        <div className="card-body py-4 text-center">
                          {/*begin::Table*/}
                          {showDispatchLog && (
                            <OrderDispatchLog
                              id={id}
                              showLog={showDispatchLog}
                            />
                          )}
                          {/*end::Table*/}
                        </div>
                        {/*end::Card body*/}
                      </div>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/*begin::Modal - Force Dispatch */}
          {popup && popup === "force-dispatch" ? (
            <div
              className="modal fade show d-block"
              id="forceDispatchModal"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-sm">
                {/*begin::Modal content*/}
                <div className="modal-content text-center bg-primary text-light">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h5 className="modal-title mx-auto text-light">
                      FORCE DISPATCH
                    </h5>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <h4 className="text-center my-5 text-light">
                      Are You Sure ?
                    </h4>
                  </div>
                  {/*end::Modal body*/}
                  {/*begin::Actions*/}
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-3"
                      onClick={() => {
                        setDriver({});
                        setError("");
                        setPopup("");
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={forceDispatch}
                      type="button"
                      className="btn btn-light btn-sm"
                      id="kt_modal_upgrade_plan_btn"
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label">Confirm</span>
                      {/*end::Indicator label*/}
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Actions*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          {/*begin::Modal - Mark Complete */}
          {popup && popup === "mark-complete" ? (
            <div
              className="modal fade show d-block"
              id="markCompleteModal"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-sm">
                {/*begin::Modal content*/}
                <div className="modal-content text-center bg-success text-light">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h5 className="modal-title mx-auto text-light">
                      MARK COMPLETE
                    </h5>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <h4 className="text-center my-5 text-light">
                      Are You Sure ?
                    </h4>
                  </div>
                  {/*end::Modal body*/}
                  {/*begin::Actions*/}
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-3"
                      onClick={() => {
                        setPopup("");
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={markComplete}
                      type="button"
                      className="btn btn-light btn-sm"
                      id="kt_modal_upgrade_plan_btn"
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label">Confirm</span>
                      {/*end::Indicator label*/}
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Actions*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          {/*begin::Modal - Cancel Trip */}
          {popup && popup === "cancel-trip" ? (
            <div
              className="modal fade show d-block"
              id="cancelTripModal"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-sm">
                {/*begin::Modal content*/}
                <div className="modal-content text-center bg-danger text-light">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h5 className="modal-title mx-auto text-light">
                      CANCEL TRIP
                    </h5>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <h4 className="text-center my-5 text-light">
                      Are You Sure ?
                    </h4>
                  </div>
                  {/*end::Modal body*/}
                  {/*begin::Actions*/}
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-3"
                      onClick={() => {
                        setPopup("");
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={cancelTrip}
                      type="button"
                      className="btn btn-light btn-sm"
                      id="kt_modal_upgrade_plan_btn"
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label">Confirm</span>
                      {/*end::Indicator label*/}
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Actions*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          {/*begin::Modal - Cancel Order */}
          {popup && popup === "cancel-order" ? (
            <div
              className="modal fade show d-block"
              id="cancelOrderModal"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-sm">
                {/*begin::Modal content*/}
                <div className="modal-content text-center bg-success text-light">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h5 className="modal-title mx-auto text-light">
                      CANCEL ORDER
                    </h5>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <h4 className="text-center my-5 text-light">
                      Are You Sure ?
                    </h4>
                  </div>
                  {/*end::Modal body*/}
                  {/*begin::Actions*/}
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-3"
                      onClick={() => {
                        setPopup("");
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={cancelOrder}
                      type="button"
                      className="btn btn-light btn-sm"
                      id="kt_modal_upgrade_plan_btn"
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label">Confirm</span>
                      {/*end::Indicator label*/}
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Actions*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          {/*begin::Modal - Scan Order */}
          {popup && popup === "scan-order" ? (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              aria-hidden="true"
            >
              {/*begin::Modal dialog*/}
              <div className="modal-dialog modal-dialog-centered modal-sm">
                {/*begin::Modal content*/}
                <div className="modal-content text-center bg-success text-light">
                  {/*begin::Modal header*/}
                  <div className="modal-header">
                    {/*begin::Close*/}
                    <h5 className="modal-title mx-auto text-light">
                      SCAN ORDER
                    </h5>
                    {/*end::Close*/}
                  </div>
                  {/*end::Modal header*/}

                  {/*begin::Modal body*/}
                  <div className="modal-body p-4">
                    <h4 className="text-center my-5 text-light">
                      Are You Sure ?
                    </h4>
                  </div>
                  {/*end::Modal body*/}
                  {/*begin::Actions*/}
                  <div className="modal-footer justify-content-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-light me-3"
                      onClick={() => {
                        setPopup("");
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={scanOrder}
                      type="button"
                      className="btn btn-light btn-sm"
                      id="kt_modal_upgrade_plan_btn"
                    >
                      {/*begin::Indicator label*/}
                      <span className="indicator-label">Confirm</span>
                      {/*end::Indicator label*/}
                      {/*end::Indicator progress*/}
                    </button>
                  </div>
                  {/*end::Actions*/}
                </div>
                {/*end::Modal content*/}
              </div>
              {/*end::Modal dialog*/}
            </div>
          ) : (
            ""
          )}
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default OrderDetails;
